import { Plus } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Field, Steps, NextStep, Callout, RoleBadge, pathFor,
  Diagram, MockField, MockSelect, MockButton, MockRow,
} from "../shared";

export default function FormsRegisterSection() {
  return (
    <div data-testid="guide-forms-register">
      <PageHeading
        icon={Plus}
        title="Register an Action Item"
        description="This form creates a brand-new task in the system from scratch. You give it a title, say who should do it and by when, and the system starts tracking it."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="admin" />
        <span className="text-xs text-white/40">General Admin and above. Open it from the header → Register, or the "+ Register" button on the Summary page.</span>
      </div>

      <Callout variant="info" title="In plain words">
        Think of this as <span className="text-white">opening a new job ticket</span>. You fill in what needs doing, hand it to a person, and the system remembers it, reminds people, and shows it on the dashboards.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-sm text-white/65 space-y-1.5 ml-2">
          <li>For a one-off task that did <span className="text-white">not</span> come out of a meeting.</li>
          <li>Tasks that come from a meeting are usually added in bulk on the Meeting Tasks page — you don't need this form for those.</li>
        </ul>
      </Section>

      <Section title="What the form looks like">
        <Diagram
          frameLabel="Register Action Item"
          legend={[
            <>Type a short <span className="text-white">title</span> — what needs to be done, in a few words.</>,
            <>Pick the <span className="text-white">authority lane</span> — this decides the review path.</>,
            <>Choose the <span className="text-white">person</span> who will own the task.</>,
            <>Set the <span className="text-white">due date</span>.</>,
            <>Write the full <span className="text-white">instruction</span> — the brief the person works to.</>,
            <>Click <span className="text-white">Register Action Item</span> to save.</>,
          ]}
        >
          <MockField label="Title" placeholder="Prepare Q3 portfolio review pack for ExCom" marker={1} />
          <MockRow>
            <MockSelect label="Authority lane" value="Board / ExCom" marker={2} />
            <MockSelect label="Assigned to" value="Amal R." marker={3} />
            <MockField label="Due date" value="30 Jun 2026" marker={4} />
          </MockRow>
          <MockField label="Instruction" placeholder="Full description of what needs to be done…" marker={5} />
          <MockButton label="Register Action Item" marker={6} />
        </Diagram>
      </Section>

      <Section title="Step-by-step">
        <Steps
          steps={[
            <>Open the <span className="text-white">Register</span> page from the header.</>,
            <>Pick the <span className="text-white">authority lane</span> the task belongs to.</>,
            <>Choose who it is <span className="text-white">assigned to</span> (the owner).</>,
            <>Set the <span className="text-white">due date</span>.</>,
            <>Write a clear <span className="text-white">instruction</span> so the owner knows exactly what to do.</>,
            <>Click <span className="text-white">Register Action Item</span> — you'll land on the new task's page.</>,
          ]}
        />
      </Section>

      <Section title="Every box explained">
        <Field name="Title" required="required">
          <p>A short, scannable summary of the item — what needs to be done. Aim for 6–12 words.</p>
          <p className="text-white/40">Example: "Prepare Q3 portfolio review pack for ExCom"</p>
        </Field>

        <Field name="Authority Lane" required="required">
          <p>Which lane this belongs to — EDGE Chairman, EDGE MD Office, Board / ExCom, or SDF MD. It decides the review path and whether the item goes through Awaiting Dispatch.</p>
          <p className="text-white/40">If unsure, check where the item is heading. Items going to a board go to Board / ExCom. Items addressed to the EDGE MD go to EDGE MD Office.</p>
        </Field>

        <Field name="Origin Type" required="required">
          <p>What kind of source this item came from — Chairman Directive, Board Resolution, IC Decision, etc. The list is set up by your Super Admin.</p>
        </Field>

        <Field name="Origin Reference" required="optional">
          <p>The actual reference number from the source (e.g. "BOR-2026-014"). Helps trace items back to source documents.</p>
        </Field>

        <Field name="Meeting Name" required="optional">
          <p>If this came from a meeting, the meeting's short name (e.g. "Q2 Board Meeting"). It appears on every email about the item and groups it on the Origins page.</p>
        </Field>

        <Field name="Assigned To" required="required">
          <p>The owner of this item — who is accountable from start to finish. Pick from the team members list; their team and function fill in automatically.</p>
          <p className="text-white/40">See <Link href={pathFor("responsibility")} className="text-cyan-300 hover:underline">Responsibility &amp; Handoffs</Link>.</p>
        </Field>

        <Field name="Currently With" required="optional">
          <p>Who has the ball right now. Defaults to the assignee. If work needs to start somewhere else (a reviewer, a lane), set it here.</p>
        </Field>

        <Field name="Due Date" required="required">
          <p>When this needs to be done. Used to colour-code overdue items on the Summary and Updates pages.</p>
        </Field>

        <Field name="Priority" required="required">
          <p>P1 / P2 / P3 / P4 (or whatever the active reference list defines). Drives the sort order on Summary.</p>
        </Field>

        <Field name="Sign-off Required" required="optional">
          <p>Turn ON if this item needs an explicit sign-off before it can be closed. The system will block any attempt to close it without one.</p>
        </Field>

        <Field name="Review Required" required="optional">
          <p>Turn ON if this item must pass through MD review (it can't skip straight from In Progress to Secretariat). Usually pre-set by the lane.</p>
        </Field>

        <Field name="Instruction" required="required">
          <p>The full description of what needs to be done. Be specific — this is the brief the assignee works to. Include context, the expected output, and any deadlines beyond the due date.</p>
          <p className="text-white/40">This text appears on the assignee's Updates page and in the registration email.</p>
        </Field>
      </Section>

      <NextStep>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>The item is created in <span className="text-white">Registered</span> status.</li>
          <li>The assignee is emailed (if email is set up and they have an address on file).</li>
          <li>You land on the item's detail page, where you can set it to In Progress, hand it off, or add a sub-task.</li>
          <li>It appears on Summary (under its lane), Updates (for the assignee), and Origins (under its origin type and meeting name).</li>
        </ul>
      </NextStep>

      <Callout variant="info" title="Which boxes are required can change">
        Your Super Admin can decide which fields are required, optional, or hidden under <span className="text-white">Admin → Field Permissions</span>. The form updates itself to match those settings.
      </Callout>
    </div>
  );
}
